"use client";

import React, { useState } from "react";
import { Clock, Flame, Star, User, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function HeroConsumer({
  selectedTab
}: {
  selectedTab: string;
}) {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <div className="lg:col-span-8 space-y-6">
          <div className="aspect-video w-full animate-pulse rounded-lg bg-muted" />
          <div className="h-8 w-3/4 animate-pulse rounded-md bg-muted" />
        </div>
        <div className="lg:col-span-4 space-y-4">
          {[...Array(3)].map((_, i) => <div key={i} className="flex gap-4">
              <div className="size-20 shrink-0 animate-pulse rounded-md bg-muted" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-full animate-pulse rounded-md bg-muted" />
              </div>
            </div>)}
        </div>
      </div>;
  }
  if (error || posts.length === 0) return null;
  const featuredPost = posts[0];
  const sidebarPosts = posts.slice(1, 4);
  return <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
      <div className="lg:col-span-8">
        <Link to={featuredPost.link || "#"} className="group block">
          <div className="relative aspect-video overflow-hidden rounded-lg bg-muted">
            {featuredPost.featuredImage && <img src={featuredPost.featuredImage} alt={featuredPost.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />}
            {featuredPost.categories?.[0] && <div className="absolute top-4 left-4">
                <span className="rounded-md bg-primary px-3 py-1 text-xs font-bold uppercase text-primary-foreground">
                  {featuredPost.categories[0].name}
                </span>
              </div>}
          </div>
          <h2 className="mt-4 text-2xl font-bold leading-tight text-foreground md:text-4xl group-hover:text-primary transition-colors">
            {featuredPost.title}
          </h2>
          <p className="mt-2 text-base text-foreground/80 line-clamp-2">
            {featuredPost.excerpt?.replace(/<[^>]*>/g, '')}
          </p>
          <div className="mt-4 flex items-center gap-4 text-sm text-foreground/60">
            <div className="flex items-center gap-1">
              <User className="size-4" />
              {featuredPost.author?.name}
            </div>
            <div className="flex items-center gap-1">
              <Clock className="size-4" />
              {new Date(featuredPost.date || "").toLocaleDateString('id-ID', {
              day: 'numeric',
              month: 'long'
            })}
            </div>
          </div>
        </Link>
      </div>

      <div className="lg:col-span-4 space-y-6">
        {sidebarPosts.map((post, i) => <Link key={post.id} to={post.link || "#"} data-index={i} className="group flex gap-4 items-start">
            <div className="size-20 shrink-0 overflow-hidden rounded-md bg-muted">
              {post.featuredImage && <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110" loading="lazy" />}
            </div>
            <div className="flex-1">
              <h4 className="line-clamp-2 text-sm font-bold leading-snug text-foreground group-hover:text-primary transition-colors">
                {post.title}
              </h4>
              <span className="mt-1 block text-xs text-foreground/50">
                {new Date(post.date || "").toLocaleDateString('id-ID', {
              day: 'numeric',
              month: 'short'
            })}
              </span>
            </div>
          </Link>)}
      </div>
    </div>;
}
export default function Hero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [selectedTab, setSelectedTab] = useState("terbaru");
  const wp_query = new WP_Query({
    post_type: "post",
    per_page: 4,
    orderby: selectedTab === "populer" ? "comment_count" : "date",
    order: "desc",
    category_name: selectedTab === "pilihan" ? "pilihan-redaksi" : undefined
  });
  const tabs = [{
    id: "terbaru",
    label: "Terbaru",
    icon: Clock
  }, {
    id: "populer",
    label: "Populer",
    icon: Flame
  }, {
    id: "pilihan",
    label: "Pilihan Redaksi",
    icon: Star
  }];
  return <section id="hero-berita-utama" className="bg-background py-16">
      <div className="container mx-auto px-4">
        <div ref={ref} className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground">BERANDA BERITA</h1>
            <p className="text-foreground/60">Sajian berita terkini, akurat dan terpercaya dari seluruh Indonesia untuk Anda.</p>
          </div>

          <div className="mb-8 flex flex-wrap gap-4">
            {tabs.map(tab => {
            const Icon = tab.icon;
            return <Button key={tab.id} onClick={() => setSelectedTab(tab.id)} variant={selectedTab === tab.id ? "default" : "outline"} className="px-6 py-2">
                  <Icon className="size-4" />
                  {tab.label}
                </Button>;
          })}
          </div>

          <PostsProvider wp_query={wp_query}>
            <HeroConsumer selectedTab={selectedTab} />
          </PostsProvider>
        </div>
      </div>
    </section>;
}